package dev.wyns.ventrova.data.sqlite

import kotlin.js.Promise
import kotlinx.coroutines.await
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array

/** Opens databases in memory with sql.js, a WebAssembly build of SQLite. */
class SqlJsDatabaseOpener : SqlDatabaseOpener {
    override suspend fun open(name: String, bytes: ByteArray): SqlDatabase {
        val sqlJs = initSqlJs(
            js("{}").unsafeCast<SqlJsConfig>().apply { locateFile = { _ -> sqlWasmUrl } },
        ).await()
        val data = bytes.unsafeCast<Int8Array>().let { Uint8Array(it.buffer, it.byteOffset, it.length) }
        return SqlJsDatabase(sqlJs.newDatabase(data))
    }
}

private class SqlJsDatabase(private val database: SqlJsNativeDatabase) : SqlDatabase {
    override suspend fun query(sql: String, vararg args: String): List<SqlRow> {
        val result = database.exec(sql, args.unsafeCast<Array<Any?>>()).firstOrNull() ?: return emptyList()
        return result.values.map { row ->
            SqlRow(
                result.columns.withIndex().associate { (index, column) ->
                    val value: dynamic = row[index]
                    column to when (jsTypeOf(value)) {
                        "number" -> value.unsafeCast<Double>()
                        "string" -> value.unsafeCast<String>()
                        else -> null
                    }
                },
            )
        }
    }

    override fun close() = database.close()
}

@JsModule("sql.js")
private external fun initSqlJs(config: SqlJsConfig): Promise<SqlJsStatic>

@JsModule("sql.js/dist/sql-wasm.wasm")
private external val sqlWasmUrl: String

private external interface SqlJsConfig {
    var locateFile: (String) -> String
}

private external interface SqlJsStatic

private fun SqlJsStatic.newDatabase(data: Uint8Array): SqlJsNativeDatabase {
    val constructor: dynamic = asDynamic().Database
    return js("new constructor(data)").unsafeCast<SqlJsNativeDatabase>()
}

private external interface SqlJsNativeDatabase {
    fun exec(sql: String, params: Array<Any?>): Array<SqlJsQueryResult>
    fun close()
}

private external interface SqlJsQueryResult {
    val columns: Array<String>
    val values: Array<Array<Any?>>
}
